/**
 * MassageAssessment — therapist-side SOAP intake (Part 2: Objective + Assessment + Plan).
 * Iter 72.
 *
 * Reads:
 *   GET /api/massage/intake/{patient_id}     — full SOAP doc (incl. patient's Subjective)
 *   GET /api/patients/{patient_id}           — patient meta for the header
 *
 * Writes:
 *   PUT /api/massage/intake/{patient_id}     — {objective: {...}} payload
 *
 * Mounted at /admin/patients/:patient_id/massage-assessment.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Save, Loader2, Sparkles, ClipboardCheck, ListChecks, FileSignature, HeartHandshake, FileDown, Mail } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../components/SignaturePad';
import useAutosave, { AutosaveIndicator } from '../../hooks/useAutosave';
import { safeConfirm } from '../../lib/confirm';

const TECHNIQUES = [
  'Effleurage', 'Petrissage', 'Tapotement', 'Friction', 'Vibration',
  'Trigger-point therapy', 'Myofascial release', 'Cross-fibre friction',
  'Stretch / PNF', 'Sports massage', 'Deep tissue', 'Lymphatic drainage',
  'Reflexology', 'Hot/cold stones', 'Aromatherapy', 'Cupping (manual)',
];

const FREQUENCIES = [
  ['weekly', 'Weekly'],
  ['biweekly', 'Every 2 weeks'],
  ['monthly', 'Monthly'],
  ['as_needed', 'As needed'],
  ['discharge', 'No follow-up planned'],
];

const PRESSURES = [
  ['light', 'Light'],
  ['medium', 'Medium'],
  ['firm', 'Firm'],
  ['deep', 'Deep'],
];

const EMPTY = {
  tissue_condition: '',
  palpation_findings: '',
  postural_findings: '',
  rom_findings: '',
  techniques_used: [],
  techniques_notes: '',
  duration_min: '',
  pressure_used: '',
  areas_worked: [],
  impression: '',
  response_to_previous: '',
  contraindications_noted: '',
  frequency_recommendation: '',
  focus_next_visit: '',
  home_care_stretches: '',
  referral_recommended: false,
  referral_details: '',
  therapist_signature: '',
  therapist_signed_at: '',
  therapist_name: '',
};

const BODY_AREAS = ['head', 'face', 'neck', 'shoulders', 'upper-back', 'mid-back', 'lower-back', 'chest', 'arms', 'forearms', 'hands', 'hips', 'glutes', 'thighs', 'knees', 'calves', 'feet'];

export default function MassageAssessment() {
  const { patient_id } = useParams();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [subjective, setSubjective] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { lastSavedAt, restore, clear } = useAutosave(`massage-objective:${patient_id}`, form);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/massage/intake/${patient_id}`),
          api.get(`/patients/${patient_id}`).catch(() => ({ data: null })),
        ]);
        setSubjective(r1.data?.subjective || null);
        const obj = r1.data?.objective || {};
        const serverClean = Object.fromEntries(
          Object.entries(obj).map(([k, v]) => [k, v == null ? EMPTY[k] : v])
        );
        const draft = restore();
        setForm({ ...EMPTY, ...serverClean, ...(draft || {}) });
        setPatient(r2.data);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm(f => ({
    ...f, [k]: (f[k] || []).includes(v) ? f[k].filter(x => x !== v) : [...(f[k] || []), v],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        objective: {
          ...form,
          duration_min: form.duration_min ? parseInt(form.duration_min) : null,
          therapist_name: form.therapist_name || user?.name || '',
        },
      };
      await api.put(`/massage/intake/${patient_id}`, payload);
      clear();
      toast.success('Session note saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // Iter 73 — Download / email the SOAP session PDF. Patient receives a
  // pre-written summary with techniques used + home-care recommendations.
  const [emailing, setEmailing] = useState(false);
  const downloadPdf = () => {
    // Server streams the PDF with our session cookie; the new tab fires the GET.
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    window.open(`${base}/api/massage/intake/${patient_id}/pdf`, '_blank');
  };
  const emailPdf = async () => {
    if (!await safeConfirm(`Email the session note to ${patient?.full_name || 'the patient'}?`)) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/massage/intake/${patient_id}/send-email`);
      if (data?.resend_configured === false) {
        toast.info('Email service not configured — PDF was rendered but no email was sent. Ask the admin to set RESEND_API_KEY.');
      } else {
        toast.success(`Sent to ${data?.to}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed.');
    } finally { setEmailing(false); }
  };

  const subjSummary = useMemo(() => {
    if (!subjective) return null;
    const bits = [];
    if (subjective.reason_for_visit) bits.push({ k: 'Reason', v: subjective.reason_for_visit });
    if (typeof subjective.pain_intensity === 'number') bits.push({ k: 'Pain (0-10)', v: subjective.pain_intensity });
    if (subjective.client_goals) bits.push({ k: 'Goals', v: subjective.client_goals });
    if (subjective.pressure_preference) bits.push({ k: 'Pressure pref', v: subjective.pressure_preference });
    if (subjective.areas_to_avoid?.length) bits.push({ k: 'Avoid', v: subjective.areas_to_avoid.join(', ') });
    if (subjective.changes_since_last_session) bits.push({ k: 'Changes since last', v: subjective.changes_since_last_session });
    return bits;
  }, [subjective]);

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">MASSAGE THERAPY · OBJECTIVE / ASSESSMENT / PLAN</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="ma-title">Massage assessment — Part 2</h1>
          <p className="text-fg-muted text-sm mt-1">
            {patient?.full_name ? <>Patient: <strong>{patient.full_name}</strong> · </> : null}
            Complete after the session to record findings, techniques used, your impression and the plan.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <AutosaveIndicator lastSavedAt={lastSavedAt} onClear={clear}/>
          <button onClick={downloadPdf}
                  className="btn-secondary inline-flex items-center gap-2 text-sm"
                  data-testid="ma-pdf-download">
            <FileDown className="w-4 h-4"/> PDF
          </button>
          <button onClick={emailPdf} disabled={emailing}
                  className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                  data-testid="ma-pdf-email">
            {emailing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
            Email session note
          </button>
          <button onClick={save} disabled={saving}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="ma-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            {saving ? 'Saving…' : 'Save session'}
          </button>
        </div>
      </div>

      {/* S — Patient subjective (read-only summary so the therapist doesn't have to flip tabs) */}
      {subjSummary && subjSummary.length > 0 && (
        <Card icon={HeartHandshake} title="S — Subjective (from patient)" tint="blue">
          <ul className="text-sm space-y-1.5" data-testid="ma-subjective-summary">
            {subjSummary.map(({ k, v }) => (
              <li key={k} className="flex gap-2">
                <span className="text-fg-muted font-medium min-w-[120px]">{k}:</span>
                <span className="flex-1">{String(v)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* O — Objective */}
      <Card icon={ClipboardCheck} title="O — Objective (your findings)">
        <Field label="Tissue condition (hypertonicity, trigger points, edema, …)">
          <textarea className="input min-h-[70px]" value={form.tissue_condition}
                    onChange={(e) => set('tissue_condition', e.target.value)}
                    placeholder="e.g. hypertonic upper trapezius, latent TrP in left levator scapulae…"
                    data-testid="ma-tissue"/>
        </Field>
        <Field label="Palpation findings">
          <textarea className="input min-h-[60px]" value={form.palpation_findings}
                    onChange={(e) => set('palpation_findings', e.target.value)}
                    placeholder="e.g. taut bands C7-T2 paraspinals; tenderness at suboccipital line…"
                    data-testid="ma-palpation"/>
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Postural findings">
            <textarea className="input min-h-[60px]" value={form.postural_findings}
                      onChange={(e) => set('postural_findings', e.target.value)}
                      placeholder="e.g. forward head posture, elevated right shoulder…"
                      data-testid="ma-postural"/>
          </Field>
          <Field label="ROM findings">
            <textarea className="input min-h-[60px]" value={form.rom_findings}
                      onChange={(e) => set('rom_findings', e.target.value)}
                      placeholder="e.g. cervical rotation R limited 30°, painful end-range…"
                      data-testid="ma-rom"/>
          </Field>
        </div>

        <Field label="Techniques used">
          <ChipRow options={TECHNIQUES} selected={form.techniques_used}
                   onToggle={(v) => toggleArr('techniques_used', v)} testid="ma-techniques"/>
        </Field>
        <Field label="Technique notes (additional detail)">
          <textarea className="input min-h-[60px]" value={form.techniques_notes}
                    onChange={(e) => set('techniques_notes', e.target.value)}
                    placeholder="e.g. sustained pressure on TrP for 60s, eased on second pass…"
                    data-testid="ma-tech-notes"/>
        </Field>

        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Session duration (min)">
            <input type="number" className="input" value={form.duration_min}
                   onChange={(e) => set('duration_min', e.target.value)}
                   data-testid="ma-duration"/>
          </Field>
          <Field label="Pressure used">
            <select className="input" value={form.pressure_used}
                    onChange={(e) => set('pressure_used', e.target.value)}
                    data-testid="ma-pressure">
              <option value="">—</option>
              {PRESSURES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Areas worked">
            <ChipRow options={BODY_AREAS} selected={form.areas_worked}
                     onToggle={(v) => toggleArr('areas_worked', v)} testid="ma-areas"/>
          </Field>
        </div>
      </Card>

      {/* A — Assessment */}
      <Card icon={Sparkles} title="A — Assessment (your impression)">
        <Field label="Impression — link the subjective complaints to your objective findings">
          <textarea className="input min-h-[80px]" value={form.impression}
                    onChange={(e) => set('impression', e.target.value)}
                    placeholder="e.g. restricted cervical mobility on the right consistent with reported tension headaches; …"
                    data-testid="ma-impression"/>
        </Field>
        <Field label="Response to previous treatment (follow-up sessions only)">
          <textarea className="input min-h-[60px]" value={form.response_to_previous}
                    onChange={(e) => set('response_to_previous', e.target.value)}
                    placeholder="e.g. headache frequency down from 4/wk to 1/wk, ROM cervical R improved…"
                    data-testid="ma-response"/>
        </Field>
        <Field label="Contraindications noted (if any)">
          <textarea className="input min-h-[50px]" value={form.contraindications_noted}
                    onChange={(e) => set('contraindications_noted', e.target.value)}
                    placeholder="e.g. avoid deep work over recent surgical scar (3 weeks post-op)…"
                    data-testid="ma-contraind"/>
        </Field>
      </Card>

      {/* P — Plan */}
      <Card icon={ListChecks} title="P — Plan (what's next)">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Recommended frequency">
            <select className="input" value={form.frequency_recommendation}
                    onChange={(e) => set('frequency_recommendation', e.target.value)}
                    data-testid="ma-frequency">
              <option value="">— Select —</option>
              {FREQUENCIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Focus of next visit">
            <input className="input" value={form.focus_next_visit}
                   onChange={(e) => set('focus_next_visit', e.target.value)}
                   placeholder="e.g. continue cervical work, add upper-trap stretch protocol"
                   data-testid="ma-focus-next"/>
          </Field>
        </div>
        <Field label="Home-care / stretches">
          <textarea className="input min-h-[80px]" value={form.home_care_stretches}
                    onChange={(e) => set('home_care_stretches', e.target.value)}
                    placeholder="e.g. doorway pec stretch 2× daily, cervical SCM self-release with ball, hydrate 2L/day…"
                    data-testid="ma-homecare"/>
        </Field>
        <div className="grid md:grid-cols-2 gap-3 items-start">
          <YesNo label="Refer out?" value={form.referral_recommended}
                 onChange={(v) => set('referral_recommended', v)} testid="ma-refer"/>
          {form.referral_recommended && (
            <Field label="Referral details">
              <input className="input" value={form.referral_details}
                     onChange={(e) => set('referral_details', e.target.value)}
                     placeholder="e.g. GP review for persistent radicular pain"
                     data-testid="ma-refer-details"/>
            </Field>
          )}
        </div>
      </Card>

      {/* Therapist sign-off */}
      <Card icon={FileSignature} title="Therapist sign-off">
        <Field label="Therapist name">
          <input className="input" value={form.therapist_name}
                 onChange={(e) => set('therapist_name', e.target.value)}
                 placeholder={user?.name || 'Your name'}
                 data-testid="ma-therapist-name"/>
        </Field>
        <div>
          <div className="text-xs text-fg-muted mb-1">Signature</div>
          <SignaturePad value={form.therapist_signature}
                        onChange={(sig) => set('therapist_signature', sig)}
                        testid="ma-therapist-signature"/>
        </div>
      </Card>
    </div>
  );
}

// ---- primitives ----
function Card({ icon: Icon, title, children, tint = 'default' }) {
  const tints = {
    default: 'border-soft bg-bg-primary',
    blue:    'border-[#003D80]/40 bg-[#F0F5FA]',
  };
  return (
    <div className={`rounded-lg border p-5 mb-4 space-y-3 ${tints[tint]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-brand"/>
        <h3 className="font-heading font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      <div className="flex gap-2" data-testid={testid}>
        {[['Yes', true], ['No', false]].map(([lbl, v]) => (
          <button key={lbl} type="button" onClick={() => onChange(v)}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs border transition ${value === v ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChipRow({ options, selected = [], onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(o => {
        const on = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition ${on ? 'bg-brand text-white border-brand' : 'border-soft hover:border-brand'}`}>
            {o.replace ? o.replace('-', ' ') : o}
          </button>
        );
      })}
    </div>
  );
}
